import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Container,
  Paper,
  AppBar,
  Toolbar,
  IconButton,
  Badge,
  Chip,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Circle as CircleIcon,
} from '@mui/icons-material';
import { useParams, useNavigate } from 'react-router-dom';
import { ChatRoom, Message, EndUser } from '../types';
import { endUserApiClient } from '../utils/api';
import { endUserSocketClient } from '../utils/socket';
import MessageBubble from '../components/MessageBubble';
import EndUserMessageInput from '../components/EndUserMessageInput';

const EndUserChat: React.FC = () => {
  const { roomId } = useParams<{ roomId: string }>();
  const navigate = useNavigate();
  
  // State管理
  const [chatRoom, setChatRoom] = useState<ChatRoom | null>(null);
  const [messages, setMessages] = useState<Message[]>([]);
  const [currentUser, setCurrentUser] = useState<EndUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [typingUsers, setTypingUsers] = useState<string[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  // Refs
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // 初期化とクリーンアップ
  useEffect(() => {
    initializeChat();
    
    return () => {
      if (roomId) {
        endUserSocketClient.leaveRoom(roomId);
      }
      endUserSocketClient.disconnect();
    };
  }, [roomId]);

  // Socket.IOイベントリスナー設定
  useEffect(() => {
    if (!roomId) return;

    console.log('🔌 Socket.IOイベントリスナー設定開始 (EndUser):', roomId);

    // 新しいメッセージ受信（グローバルイベント経由）
    const handleNewMessageEvent = (event: Event) => {
      const customEvent = event as CustomEvent;
      const message: Message = customEvent.detail;
      
      console.log('📨 Socket.IOから新しいメッセージ受信 (EndUser via CustomEvent):', message);
      console.log('📨 現在のroomId:', roomId);
      console.log('📨 メッセージのroomId:', message.chat_room_id || message.room_id);
      
      // メッセージのルームIDをチェック（chat_room_id または room_id）
      const messageRoomId = message.chat_room_id || message.room_id;
      if (messageRoomId === roomId) {
        console.log('✅ ルームIDが一致、メッセージを処理します (EndUser)');
        setMessages(prev => {
          // 重複チェック（同じIDのメッセージが既に存在する場合は追加しない）
          const exists = prev.some(m => m.id === message.id);
          if (exists) {
            console.log('🔄 重複メッセージのため追加をスキップ:', message.id);
            return prev;
          }
          console.log('✅ Socket.IOメッセージを状態に追加 (EndUser):', message.id);
          return [...prev, message];
        });
        
        // 自動スクロール
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      } else {
        console.log('❌ ルームIDが不一致、メッセージをスキップ (EndUser)');
      }
    };

    // 接続状態変更
    const handleConnect = () => {
      console.log('🔌 Socket.IO接続成功 (EndUser)');
      setIsConnected(true);
      setError(null);
      
      // 接続成功後、少し待ってからルーム参加
      setTimeout(() => {
        console.log('🏠 接続成功後のルーム参加:', roomId);
        endUserSocketClient.autoJoinRoom(roomId);
      }, 1000);
    };

    const handleDisconnect = () => {
      console.log('🔌 Socket.IO接続切断 (EndUser)');
      setIsConnected(false);
    };

    // グローバルイベントリスナーを登録
    window.addEventListener('rt:newMessage', handleNewMessageEvent as EventListener);
    window.addEventListener('rt:connected', handleConnect);
    window.addEventListener('rt:disconnected', handleDisconnect);

    // Socket.IO接続開始
    console.log('🔌 Socket.IO接続開始 (EndUser)');
    endUserSocketClient.connect_socket();

    // クリーンアップ
    return () => {
      console.log('🧹 Socket.IOイベントリスナークリーンアップ (EndUser)');
      window.removeEventListener('rt:newMessage', handleNewMessageEvent as EventListener);
      window.removeEventListener('rt:connected', handleConnect);
      window.removeEventListener('rt:disconnected', handleDisconnect);
      
      if (roomId) {
        endUserSocketClient.leaveRoom(roomId);
      }
    };
  }, [roomId]);

  // 自動スクロール
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  // 初期化
  const initializeChat = async () => {
    try {
      setLoading(true);
      setError(null);

      // ユーザー情報を取得
      const { user } = endUserApiClient.getSession();
      if (!user) {
        navigate('/user/login');
        return;
      }
      setCurrentUser(user);

      if (!roomId) {
        setError('チャットルームIDが指定されていません');
        return;
      }

      // デモルームの場合は実際のルームIDを取得
      if (roomId === 'demo') {
        await handleDemoRoom();
        return;
      }

      // チャットルーム情報とメッセージを並行取得
      await Promise.all([
        loadChatRoom(),
        loadMessages()
      ]);

    } catch (err) {
      console.error('❌ 初期化エラー:', err);
      setError('チャット画面の初期化に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  // デモルーム処理
  const handleDemoRoom = async () => {
    try {
      const response = await endUserApiClient.getChatRoom('demo');
      if (response.success && response.chatRoom) {
        // 実際のルームIDにリダイレクト
        navigate(`/user/chat/${response.chatRoom.id}`, { replace: true });
      } else {
        setError('デモチャットルームの作成に失敗しました');
      }
    } catch (err) {
      console.error('❌ デモルーム取得エラー:', err);
      setError('デモチャットルームの取得に失敗しました');
    }
  };

  // チャットルーム情報読み込み
  const loadChatRoom = async () => {
    if (!roomId) return;

    try {
      const response = await endUserApiClient.getChatRoom(roomId);
      if (response.success && response.chatRoom) {
        setChatRoom(response.chatRoom);
      } else {
        setError('チャットルームが見つかりません');
      }
    } catch (err: any) {
      console.error('❌ チャットルーム取得エラー:', err);
      if (err?.response?.status === 403) {
        setError('このチャットは終了しました。この度はお問い合わせいただき、誠にありがとうございました。');
      } else {
        setError('チャットルーム情報の取得に失敗しました');
      }
    }
  };

  // メッセージ履歴読み込み
  const loadMessages = async () => {
    if (!roomId) return;

    try {
      console.log('📥 メッセージ履歴取得開始 (EndUser):', roomId);
      const response = await endUserApiClient.getMessages(roomId);
      console.log('📥 メッセージ履歴取得レスポンス:', response);
      
      if (response.success && response.messages) {
        console.log('✅ メッセージ履歴取得成功:', response.messages.length, '件');
        setMessages(response.messages);
        setTimeout(scrollToBottom, 100);
      } else {
        console.warn('⚠️ メッセージ履歴が空またはレスポンスが無効:', response);
      }
    } catch (err: any) {
      console.error('❌ メッセージ取得エラー:', err);
      if (err?.response?.status === 403) {
        setError('このチャットは終了しました。この度はお問い合わせいただき、誠にありがとうございました。');
      }
      setMessages([]);
    }
  };

  // 未読数取得
  const loadUnreadCount = async () => {
    if (!roomId) return;

    try {
      const response = await endUserApiClient.getUnreadCount(roomId);
      if (response.success && response.data) {
        setUnreadCount(response.data.count);
      }
    } catch (err) {
      console.error('❌ 未読数取得エラー:', err);
    }
  };

  // メッセージ送信
  const handleSendMessage = async (content: string, attachments?: File[]): Promise<void> => {
    if (!roomId || (!content.trim() && !attachments?.length)) return;

    try {
      console.log('📤 メッセージ送信開始 (EndUserChat):', content.trim(), attachments);
      
      // ファイルアップロード処理
      let fileMeta: { url: string; fileName?: string; originalName?: string } | undefined;
      if (attachments && attachments.length > 0) {
        const file = attachments[0];
        console.log('📤 ファイルアップロード開始:', file.name, 'サイズ:', file.size);
        
        try {
          const uploadResponse = await endUserApiClient.uploadFile(file);
          console.log('📤 ファイルアップロードレスポンス:', uploadResponse);
          
          if (uploadResponse.success) {
            // レスポンスから url, fileName, originalName を取得
            // uploadResponse.data がある場合はそれを使用、なければ直接レスポンスから取得
            const responseAny = uploadResponse as any;
            fileMeta = uploadResponse.data || {
              url: responseAny.url,
              fileName: responseAny.fileName,
              originalName: responseAny.originalName
            };
            console.log('✅ ファイルアップロード成功:', fileMeta);
          } else {
            throw new Error(uploadResponse.message || 'ファイルのアップロードに失敗しました');
          }
        } catch (uploadError) {
          console.error('❌ ファイルアップロードエラー:', uploadError);
          console.error('❌ エラーの詳細:', {
            message: uploadError instanceof Error ? uploadError.message : String(uploadError),
            response: (uploadError as any)?.response,
            status: (uploadError as any)?.response?.status,
            data: (uploadError as any)?.response?.data
          });
          
          // 詳細なエラーメッセージを作成
          let errorMessage = 'ファイルのアップロードに失敗しました';
          
          if ((uploadError as any)?.response) {
            const status = (uploadError as any).response.status;
            const data = (uploadError as any).response.data;
            
            if (status === 401) {
              errorMessage = '認証エラー: ログインが必要です';
            } else if (status === 403) {
              errorMessage = '権限エラー: アクセスが拒否されました';
            } else if (status === 413) {
              errorMessage = 'ファイルサイズが大きすぎます（上限: 20MB）\n画像の場合はリサイズオプションで「小」または「中」を選択してください';
            } else if (data?.message) {
              errorMessage = `アップロードエラー: ${data.message}`;
            } else {
              errorMessage = `アップロードエラー (${status}): サーバーとの通信に失敗しました`;
            }
          } else if (uploadError instanceof Error) {
            errorMessage = `アップロードエラー: ${uploadError.message}`;
          }
          
          setError(errorMessage);
          alert(errorMessage); // アラートで表示
          throw uploadError;
        }
      }
      
      // 1. APIでメッセージを送信（データベースに保存）
      const response = await endUserApiClient.sendMessage(
        roomId, 
        content.trim() || '（ファイル添付）', 
        fileMeta
      );
      
      console.log('📤 APIレスポンス:', response);
      
      if (response.success && response.data) {
        console.log('✅ メッセージ送信成功 (EndUserChat):', response.data);
        
        // 2. Socket.IOでリアルタイム通知（他のユーザーへの通知用）
        endUserSocketClient.sendMessage(roomId, content.trim() || '（ファイル添付）');
        
        // 3. ローカルのメッセージリストを更新
        setMessages(prev => {
          const newMessage = response.data!;
          const exists = prev.some(m => m.id === newMessage.id);
          if (exists) {
            console.log('⚠️ 重複メッセージをスキップ:', newMessage.id);
            return prev;
          }
          console.log('➕ メッセージをリストに追加:', newMessage.id);
          return [...prev, newMessage];
        });
        
        // 4. 自動スクロール
        setTimeout(() => {
          scrollToBottom();
        }, 100);
      } else {
        console.error('❌ APIレスポンスが無効:', response);
        throw new Error('メッセージの送信に失敗しました');
      }
      
      // タイピング状態をクリア
      endUserSocketClient.stopTyping(roomId);
      
    } catch (error) {
      console.error('❌ メッセージ送信エラー:', error);
      const errorMessage = error instanceof Error ? error.message : 'メッセージの送信に失敗しました';
      setError(errorMessage);
      throw error;
    }
  };

  // タイピング開始
  const handleTypingStart = () => {
    if (!roomId) return;
    
    endUserSocketClient.startTyping(roomId);
    
    // タイピング状態のタイムアウト設定
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    typingTimeoutRef.current = setTimeout(() => {
      endUserSocketClient.stopTyping(roomId);
    }, 3000);
  };

  // タイピング終了
  const handleTypingStop = () => {
    if (!roomId) return;
    
    endUserSocketClient.stopTyping(roomId);
    
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = null;
    }
  };

  // データ再読み込み
  const handleRefresh = async () => {
    await Promise.all([
      loadChatRoom(),
      loadMessages(),
      loadUnreadCount()
    ]);
  };

  // メッセージ編集
  const handleEditMessage = async (messageId: string, newContent: string) => {
    if (!roomId) return;

    try {
      const response = await endUserApiClient.editMessage(roomId, messageId, newContent);
      if (response.success && response.data) {
        // メッセージリストを更新
        setMessages(prev => 
          prev.map(msg => 
            msg.id === messageId ? { ...msg, content: newContent, is_edited: true, edited_at: new Date().toISOString() } : msg
          )
        );
      }
    } catch (error) {
      console.error('メッセージ編集エラー:', error);
      throw error; // エラーを再スローして、UIで処理
    }
  };

  // メッセージ削除
  const handleDeleteMessage = async (messageId: string) => {
    if (!roomId) return;

    try {
      const response = await endUserApiClient.deleteMessage(roomId, messageId);
      if (response.success) {
        // メッセージリストから削除
        setMessages(prev => prev.filter(msg => msg.id !== messageId));
      }
    } catch (error) {
      console.error('メッセージ削除エラー:', error);
      throw error; // エラーを再スローして、UIで処理
    }
  };

  // ローディング画面
  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="50vh">
          <CircularProgress />
          <Typography variant="body1" sx={{ ml: 2 }}>
            チャット画面を読み込んでいます...
          </Typography>
        </Box>
      </Container>
    );
  }

  // エラー画面
  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
        <Box display="flex" justifyContent="center">
          <IconButton onClick={handleRefresh} color="primary">
            <RefreshIcon />
          </IconButton>
        </Box>
      </Container>
    );
  }

  // チャットルームの送信可否判定
  const isRoomClosed = chatRoom?.status === 'completed' || chatRoom?.status === 'archived';
  const getClosedRoomMessage = () => {
    if (chatRoom?.status === 'completed') {
      return 'このチャットルームは完了しました。ご利用ありがとうございました。';
    }
    if (chatRoom?.status === 'archived') {
      return 'このチャットは終了しました。この度はお問い合わせいただき、誠にありがとうございました。今後ともよろしくお願いいたします。';
    }
    return '';
  };

  return (
    <Box sx={{ 
      height: '100dvh',
      display: 'flex', 
      flexDirection: 'column',
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      maxWidth: '768px',
      margin: '0 auto',
      overflow: 'hidden',
      backgroundColor: '#f5f5f5',
    }}>
      {/* ヘッダー */}
      <AppBar position="static" color="primary">
        <Toolbar>
          <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {/* ロゴ */}
              <Box
                component="img"
                src="/logo-horizontal.png"
                alt="マイトーク"
                sx={{
                  height: '24px',
                  width: 'auto',
                  filter: 'brightness(0) invert(1)', // 白色に変換
                }}
              />
              <Typography variant="subtitle2" sx={{ fontWeight: 600, opacity: 0.9 }}>
                {chatRoom?.title || 'チャットルーム'}
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {currentUser?.displayName}様
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* 接続状態 */}
            <Chip
              icon={<CircleIcon sx={{ fontSize: '12px !important' }} />}
              label={isConnected ? 'オンライン' : 'オフライン'}
              color={isConnected ? 'success' : 'default'}
              size="small"
              variant="outlined"
              sx={{ color: 'white', borderColor: 'rgba(255,255,255,0.5)' }}
            />

            {/* 未読数 */}
            {unreadCount > 0 && (
              <Badge badgeContent={unreadCount} color="error">
                <Box />
              </Badge>
            )}

            <IconButton color="inherit" onClick={handleRefresh}>
              <RefreshIcon />
            </IconButton>
          </Box>
        </Toolbar>
      </AppBar>

      {/* チャット詳細情報 */}
      {chatRoom && (
        <Paper elevation={1} sx={{ p: 2, m: 1, borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {chatRoom.description}
          </Typography>
        </Paper>
      )}

      {/* メッセージエリア */}
      <Box 
        sx={{ 
          flexGrow: 1, 
          overflow: 'auto', 
          p: 1,
          pb: 2, // 下部に余白を追加
          backgroundColor: '#f5f5f5',
          // Safari対応: スクロール領域を適切に設定
          WebkitOverflowScrolling: 'touch',
          // iOS Safariのツールバー対応: 最小の高さを確保
          minHeight: 0,
        }}
      >
        {messages.length === 0 ? (
          <Box 
            display="flex" 
            justifyContent="center" 
            alignItems="center" 
            height="100%"
          >
            <Typography variant="body2" color="text.secondary">
              まだメッセージがありません
            </Typography>
          </Box>
        ) : (
          <>
            {(() => {
              const filteredMessages = messages.filter(message => {
                if (message.message_type !== 'system') return true;
                const content = message.content || '';
                const isStatusChangeMessage = (
                  (content.includes('ステータスが') && content.includes('に変更されました')) ||
                  content.includes('アーカイブされました') ||
                  content.includes('削除されました')
                );
                return !isStatusChangeMessage;
              });

              const formatDateLabel = (dateStr: string) => {
                const date = new Date(dateStr);
                const days = ['日', '月', '火', '水', '木', '金', '土'];
                return `${date.getFullYear()}年${date.getMonth() + 1}月${date.getDate()}日（${days[date.getDay()]}）`;
              };

              const getDateKey = (dateStr: string) => {
                const d = new Date(dateStr);
                return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
              };

              return filteredMessages.map((message, index) => {
                const showDateSeparator = index === 0 ||
                  getDateKey(message.created_at) !== getDateKey(filteredMessages[index - 1].created_at);

                return (
                  <React.Fragment key={message.id}>
                    {showDateSeparator && (
                      <Box sx={{ display: 'flex', alignItems: 'center', my: 2, px: 2 }}>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: '#ccc' }} />
                        <Typography
                          variant="caption"
                          sx={{
                            px: 2,
                            color: '#888',
                            fontSize: '0.75rem',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          {formatDateLabel(message.created_at)}
                        </Typography>
                        <Box sx={{ flex: 1, height: '1px', bgcolor: '#ccc' }} />
                      </Box>
                    )}
                    <MessageBubble
                      message={message}
                      isCurrentUser={message.sender_type === 'end_user'}
                      showAvatar={
                        index === 0 ||
                        filteredMessages[index - 1].sender_id !== message.sender_id ||
                        filteredMessages[index - 1].sender_type !== message.sender_type
                      }
                      onMarkAsRead={() => {}}
                      onEditMessage={handleEditMessage}
                      onDeleteMessage={handleDeleteMessage}
                    />
                  </React.Fragment>
                );
              });
            })()}

            {/* タイピングインジケーター */}
            {typingUsers.length > 0 && (
              <Box sx={{ p: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  担当者が入力中...
                </Typography>
              </Box>
            )}

            <div ref={messagesEndRef} />
          </>
        )}
      </Box>

      {/* ルームクローズ通知 */}
      {isRoomClosed && (
        <Alert 
          severity="info" 
          sx={{ 
            mx: 1,
            mb: 0,
            borderRadius: 0,
          }}
        >
          {getClosedRoomMessage()}
        </Alert>
      )}

      {/* メッセージ入力エリア */}
      <Box sx={{
        // Safari対応: 入力欄を画面下部に固定
        position: 'sticky',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
        backgroundColor: '#fff',
        // iOS Safariのツールバー対応: 安全領域の確保
        paddingBottom: 'max(env(safe-area-inset-bottom), 16px)',
        boxShadow: '0 -2px 8px rgba(0,0,0,0.1)',
      }}>
        <EndUserMessageInput
          onSendMessage={handleSendMessage}
          onTypingStart={handleTypingStart}
          onTypingStop={handleTypingStop}
          disabled={!isConnected || isRoomClosed}
          placeholder={
            isRoomClosed
              ? chatRoom?.status === 'archived'
                ? "このチャットは終了しました"
                : "このチャットは完了しました"
              : isConnected 
                ? "メッセージを入力してください..." 
                : "接続中です..."
          }
        />
      </Box>
    </Box>
  );
};

export default EndUserChat; 